import type { ColumnDef } from '@tanstack/react-table'
import type { ICountry, IProvince } from '@/entities/country'
import type { IDestination } from '@/entities/destination'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DestinationDeleteButton } from '@/features/destination/destination-delete-button'
import { DestinationStatusToggle } from '@/features/destination/destination-status-toggle'
import { Badge } from '@/shared/ui/badge'
import { DataTable } from '@/shared/ui/data-table'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'

interface DestinationListTableProps {
  destinations: IDestination[]
  countries: ICountry[]
  provinces: IProvince[]
  loading: boolean
  pagination: PaginationConfigInput
  languageCode: string
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (item: IDestination) => void
  onView?: (item: IDestination) => void
  readonly?: boolean
}

/**
 * Displays destinations with localized names, status controls, and pagination.
 */
export function DestinationListTable({
  destinations,
  countries,
  provinces,
  loading,
  pagination,
  languageCode,
  onPageChange,
  onEdit,
  onView,
  readonly = false,
}: DestinationListTableProps) {
  const { t } = useTranslation()
  const countryById = useMemo(
    () =>
      Object.fromEntries(
        countries.map((item) => [item.id, item.nativeName ?? item.name ?? item.code]),
      ),
    [countries],
  )
  const provinceById = useMemo(
    () =>
      Object.fromEntries(
        provinces.map((item) => [item.id, item.nativeName ?? item.name ?? item.code]),
      ),
    [provinces],
  )

  const columns = useMemo<ColumnDef<IDestination>[]>(
    () => [
      {
        id: 'image',
        header: t('common.image', { defaultValue: 'Image' }),
        cell: ({ row }) => {
          const imageUrl = row.original.thumbnail ?? row.original.banner ?? ''
          if (!imageUrl) {
            return <span className="text-zinc-400">—</span>
          }
          return (
            <img
              src={imageUrl}
              alt=""
              className="h-10 w-16 rounded-md border border-zinc-200 object-cover"
              loading="lazy"
            />
          )
        },
      },
      {
        id: 'name',
        header: t('common.name'),
        cell: ({ row }) => {
          return resolveDashboardLocalizedValue(row.original.localizedData, languageCode)
        },
      },
      {
        accessorKey: 'slug',
        header: 'Slug',
      },
      {
        id: 'country',
        header: t('destination.country'),
        cell: ({ row }) => countryById[row.original.countryId] ?? row.original.countryId,
      },
      {
        id: 'province',
        header: t('destination.province'),
        cell: ({ row }) => provinceById[row.original.provinceId] ?? row.original.provinceId,
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) =>
          readonly ? (
            <div className="flex justify-center">
              <Badge
                variant="outline"
                className={
                  row.original.isActive
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-zinc-200 bg-zinc-50 text-zinc-500'
                }
              >
                {row.original.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            </div>
          ) : (
            <DestinationStatusToggle
              destinationId={row.original.id}
              isActive={row.original.isActive}
            />
          ),
      },
      ...(!readonly
        ? [
            {
              id: 'actions',
              size: 64,
              header: () => null,
              cell: ({ row }: { row: { original: IDestination } }) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <DestinationDeleteButton destinationId={row.original.id} />
                </div>
              ),
            },
          ]
        : []),
    ],
    [countryById, provinceById, readonly, t, languageCode],
  )
  const paginationConfig = useMemo(
    () => ({
      ...pagination,
      onChange: onPageChange,
    }),
    [onPageChange, pagination],
  )

  const handleRowClick = readonly ? onView : onEdit

  return (
    <DataTable
      columns={columns}
      data={destinations}
      loading={loading}
      emptyText={t('common.noData', { defaultValue: 'No data' })}
      pagination={paginationConfig}
      {...(handleRowClick ? { onRowClick: handleRowClick } : {})}
    />
  )
}
